import React from 'react';
import { Link } from 'react-router-dom';

export default function QuoteGenPrivacyPolicy() {
  return (
    <div className="min-h-screen bg-zinc-950 text-white">
      {/* Navigation */}
      <nav className="fixed top-0 w-full bg-zinc-950/80 backdrop-blur-md border-b border-zinc-800/50 z-50">
        <div className="max-w-4xl mx-auto px-6 py-4 flex justify-between items-center">
          <Link to="/" className="text-lg font-mono text-zinc-400 hover:text-white transition-colors">
            ← Back to Portfolio
          </Link>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-20 px-6">
        <div className="max-w-4xl mx-auto">
          <div className="mb-12">
            <h1 className="text-4xl md:text-5xl font-bold mb-4">Privacy Policy</h1>
            <p className="text-zinc-400 text-lg">QuoteGen - AI Quote Generator for Contractors</p>
            <p className="text-zinc-500 text-sm mt-2">Last updated: January 10, 2026</p>
          </div>

          <div className="space-y-10 text-zinc-300 leading-relaxed">
            {/* Introduction */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Introduction</h2>
              <p>
                Welcome to QuoteGen ("we," "our," or "us"). We are committed to protecting your privacy. 
                This Privacy Policy explains how we handle information when you use our mobile application 
                ("App"), an AI-powered quote generator designed to help contractors create professional 
                estimates for construction work.
              </p>
            </section>

            {/* Information We Collect */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Information We Collect</h2>
              <p className="mb-4">
                <strong className="text-white">We do not collect any personal information.</strong>
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>We do not require you to create an account</li>
                <li>We do not collect your name, email address, or any contact information</li>
                <li>We do not collect payment information</li>
                <li>We do not track your location</li>
                <li>We do not use cookies or similar tracking technologies</li>
                <li>We do not collect device identifiers or usage analytics</li>
              </ul>
            </section>

            {/* How the App Works */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">How the App Works</h2>
              <p>
                QuoteGen uses artificial intelligence to help you generate professional quotes for 
                construction and contractor work. When you use the App to generate a quote, the 
                information you enter (such as project descriptions, materials, and labor details) 
                is processed to create your quote. This information is processed locally on your 
                device and through our AI service provider.
              </p>
            </section>

            {/* Third-Party Services */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Third-Party Services</h2>
              <p className="mb-4">
                Our App uses the OpenAI API to power the AI quote generation functionality. When you 
                generate a quote, the project details you enter are sent to OpenAI's servers for processing.
              </p>
              <p className="mb-4">
                According to OpenAI's policies:
              </p>
              <ul className="list-disc list-inside space-y-2 ml-4">
                <li>
                  API data is not used to train OpenAI's models by default
                </li>
                <li>
                  OpenAI may retain API inputs and outputs for up to 30 days for abuse and misuse 
                  monitoring purposes, after which it is deleted (unless legally required to retain it)
                </li>
                <li>
                  OpenAI implements security measures to protect data during transmission and storage
                </li>
              </ul>
              <p className="mt-4">
                For more information about how OpenAI handles data, please review{' '}
                <a 
                  href="https://openai.com/policies/privacy-policy" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  OpenAI's Privacy Policy
                </a>{' '}
                and{' '}
                <a 
                  href="https://openai.com/policies/api-data-usage-policies" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  API Data Usage Policies
                </a>.
              </p>
            </section>

            {/* Data Storage */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Data Storage</h2>
              <p>
                Any quotes or project information you create within the App are stored locally on 
                your device only. We do not have access to this data, and it is not transmitted 
                to our servers. If you delete the App, any locally stored data will be removed 
                from your device.
              </p>
            </section>

            {/* Children's Privacy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Children's Privacy</h2>
              <p>
                Our App is designed for professional contractors and is not intended for use by 
                children under the age of 13. We do not knowingly collect any information from 
                children under 13.
              </p>
            </section>

            {/* Changes to This Policy */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Changes to This Privacy Policy</h2>
              <p>
                We may update this Privacy Policy from time to time. If we make changes, we will 
                notify you by revising the "Last updated" date at the top of this policy. We 
                encourage you to review this Privacy Policy periodically to stay informed about 
                how we are protecting your information.
              </p>
            </section>

            {/* Contact */}
            <section>
              <h2 className="text-2xl font-semibold text-white mb-4">Contact Us</h2>
              <p>
                If you have any questions about this Privacy Policy or our privacy practices, 
                please contact us at:
              </p>
              <p className="mt-4">
                <a 
                  href="mailto:parkerdevwork@gmail.com" 
                  className="text-blue-400 hover:text-blue-300 underline"
                >
                  parkerdevwork@gmail.com
                </a>
              </p>
            </section>

            {/* Summary */}
            <section className="bg-zinc-900/50 border border-zinc-800/50 rounded-lg p-6 mt-12">
              <h2 className="text-xl font-semibold text-white mb-4">Summary</h2>
              <p className="text-zinc-400">
                In short: We don't collect your data. We don't require accounts. We don't track you. 
                The only data processing occurs when you generate quotes, which is handled by OpenAI's 
                API according to their privacy policies. Your quotes are stored only on your device.
              </p>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-6 border-t border-zinc-800/50">
        <div className="max-w-4xl mx-auto text-center text-sm text-zinc-600">
          <p>© 2026 QuoteGen. All rights reserved.</p>
        </div>
      </footer>
    </div>
  );
}
